import * as React from 'react';

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/core/combobox';

export type TagsInputProps = {
  /** Expected to be trimmed and free of duplicates; the component emits values in this form. */
  value: string[];
  onValueChange: (value: string[]) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  caseSensitive?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
};

function toComparisonValue(value: string, caseSensitive: boolean) {
  const trimmedValue = value.trim();
  return caseSensitive ? trimmedValue : trimmedValue.toLocaleLowerCase();
}

function containsTag(tags: string[], tag: string, caseSensitive: boolean) {
  const comparisonValue = toComparisonValue(tag, caseSensitive);

  return tags.some(
    (existingTag) => toComparisonValue(existingTag, caseSensitive) === comparisonValue,
  );
}

function normalizeTags(tags: string[], caseSensitive: boolean) {
  return tags.reduce<string[]>((normalizedTags, tag) => {
    const trimmedTag = tag.trim();

    if (!trimmedTag || containsTag(normalizedTags, trimmedTag, caseSensitive)) {
      return normalizedTags;
    }

    return [...normalizedTags, trimmedTag];
  }, []);
}

function splitPastedTags(value: string) {
  return value.split(/[,\n\r]+/);
}

function hasTagDelimiters(value: string) {
  return /[,\n\r]/.test(value);
}

function tagsAreEqual(left: string[], right: string[]) {
  return left.length === right.length && left.every((tag, index) => tag === right[index]);
}

export function TagsInput({
  value,
  onValueChange,
  onBlur,
  id,
  placeholder,
  disabled = false,
  caseSensitive = false,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
  'aria-invalid': ariaInvalid,
}: TagsInputProps) {
  const [inputValue, setInputValue] = React.useState('');
  const anchorRef = useComboboxAnchor();
  const items = React.useMemo(() => {
    const trimmedInputValue = inputValue.trim();

    if (!trimmedInputValue || containsTag(value, trimmedInputValue, caseSensitive)) {
      return value;
    }

    return [trimmedInputValue, ...value];
  }, [caseSensitive, inputValue, value]);

  const emitNormalizedValue = (nextValue: string[]) => {
    if (disabled) return;

    const normalizedValue = normalizeTags(nextValue, caseSensitive);

    if (!tagsAreEqual(normalizedValue, value)) {
      onValueChange(normalizedValue);
    }
  };

  const commitInputValue = () => {
    const trimmedValue = inputValue.trim();

    setInputValue('');

    if (!trimmedValue) return;

    emitNormalizedValue([...value, trimmedValue]);
  };

  return (
    <Combobox
      // Only auto-highlight while there is a real query; Base UI trims the query
      // and shows the full unfiltered list for whitespace-only input, so a
      // standing highlight there would let Enter toggle off an arbitrary tag.
      autoHighlight={inputValue.trim() !== ''}
      disabled={disabled}
      filter={(itemValue, query, itemToString) => {
        const itemLabel = itemToString?.(itemValue) ?? String(itemValue);
        const normalizedItemLabel = caseSensitive ? itemLabel : itemLabel.toLocaleLowerCase();
        const normalizedQuery = caseSensitive ? query.trim() : query.trim().toLocaleLowerCase();

        if (!normalizedQuery) return false;

        return normalizedItemLabel.includes(normalizedQuery);
      }}
      inputValue={inputValue}
      items={items}
      multiple
      value={value}
      onInputValueChange={(nextInputValue) => {
        setInputValue(nextInputValue);
      }}
      onValueChange={(nextValue, eventDetails) => {
        if (!Array.isArray(nextValue)) return;

        // Base UI clears the entire selection on Escape when the popup is closed;
        // never treat that as a tag change.
        if (eventDetails.reason === 'escape-key') return;

        emitNormalizedValue(nextValue);
        setInputValue('');
      }}
    >
      <ComboboxChips ref={anchorRef} className={className}>
        <ComboboxValue>
          {(selectedValues: string[]) => (
            <>
              {selectedValues.map((selectedValue) => (
                <ComboboxChip key={selectedValue} showRemove={!disabled}>
                  {selectedValue}
                </ComboboxChip>
              ))}
              <ComboboxChipsInput
                aria-describedby={ariaDescribedby}
                aria-invalid={ariaInvalid}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                disabled={disabled}
                id={id}
                placeholder={selectedValues.length === 0 ? placeholder : undefined}
                onBlur={onBlur}
                onKeyDown={(event) => {
                  if (event.key === ',') {
                    event.preventDefault();
                    commitInputValue();
                  }
                }}
                onPaste={(event) => {
                  const pastedText = event.clipboardData.getData('text');

                  if (!hasTagDelimiters(pastedText)) return;

                  event.preventDefault();

                  const { selectionStart, selectionEnd } = event.currentTarget;
                  const combinedText =
                    inputValue.slice(0, selectionStart ?? inputValue.length) +
                    pastedText +
                    inputValue.slice(selectionEnd ?? inputValue.length);

                  setInputValue('');
                  emitNormalizedValue([...value, ...splitPastedTags(combinedText)]);
                }}
              />
            </>
          )}
        </ComboboxValue>
      </ComboboxChips>
      {items.length > 0 && (
        <ComboboxContent anchor={anchorRef}>
          <ComboboxList>
            {(tag: string) => (
              <ComboboxItem key={tag} value={tag}>
                {tag}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      )}
    </Combobox>
  );
}
